import { useEffect, useState, useRef } from "react";
import { useAppSelector } from "hooks/redux";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { Controller } from "react-hook-form";
import { useLazyQuery, gql } from "@apollo/client";
import FieldError from "shared/organisms/dynamicform/FieldError";
import { StyledSelect } from "./styles";
import liveClassClientService from "utils/graphql/liveclassClient";
import { makeStyles } from "@mui/styles";

const INITIAL_DATA_LIMIT = 20;
const DUMMY_QUERY = gql`
  query DummyQuery {
    _dummy
  }
`;

interface ISingleSelectProps {
  field: any;
  control: any;
  errors: any;
  getValues?: any;
  setValue?: any;
  watch?: any;
}

const useStyles = makeStyles({
  border: {
    "& .css-m1opko-MuiFormLabel-root-MuiInputLabel-root": {
      background: "white",
      paddingRight: "5px",
      paddingLeft: "5px",
    },
  },
});

const SingleSelectWithSearch = ({
  field,
  control,
  errors,
  setValue,
  watch,
}: ISingleSelectProps) => {
  const [options, setOptions] = useState<any[]>([]);
  const [initialOptions, setInitialOptions] = useState<any[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const selectedOptionRef = useRef<{ label: string; value: string } | null>(
    null
  );
  const debounceRef = useRef<any>(null);
  const prevParentValueRef = useRef<any>(undefined);

  const classes = useStyles();

  const parentValue = field.dependentOn
    ? watch?.(field.dependentOn)
    : null;

  const parentValueStr = JSON.stringify(parentValue);

  const isParentEmpty = Array.isArray(parentValue)
    ? parentValue.every((v: any) => !v)
    : !parentValue;

  const getDependentPayload = () => {
    if (!field.dependentOn || isParentEmpty) return {};
    
    if (field.formatDependentPayload) {
      return field.formatDependentPayload(parentValue);
    }

    if (Array.isArray(field.dependentOn)) {
      return field.dependentOn.reduce((acc: any, key: string, index: number) => {
        let val = parentValue[index];
        if (val === "true") val = true;
        if (val === "false") val = false;
        
        if (val !== undefined && val !== null && val !== "") {
          acc[key] = val;
        }
        return acc;
      }, {});
    }
    
    let singleVal = parentValue;
    if (singleVal === "true") singleVal = true;
    if (singleVal === "false") singleVal = false;
    
    return { [field.dependentOn]: singleVal };
  };

  // Read from Redux metadata when field.list is provided
  const metaData = useAppSelector((state: any) => state.metadataReducer?.data);
  const metaListRaw = field.list ? metaData?.[field.list] : null;

  // const [getData] = useLazyQuery(field.query || null, {
  //   client: liveClassClientService,
  //   fetchPolicy: "no-cache",
  // });
  const [getData] = useLazyQuery(field.query || DUMMY_QUERY, {
    client: liveClassClientService,
    fetchPolicy: "no-cache",
  });

  const convertToLabelValue = (items: any[]) => {
    if (!items || items.length === 0) return [];
    return items.map((item: any) => ({
      label: item[field.keyValue.label],
      value: item[field.keyValue.value],
    }));
  };

  const mergeSelectedOption = (list: any[]) => {
    const selected = selectedOptionRef.current;
    if (!selected) return list;
    const exists = list.some((o: any) => o.value === selected.value);
    return exists ? list : [selected, ...list];
  };

  // Sync options from Redux metadata when field.list is provided
  useEffect(() => {
    if (!field.list) return;
    const mapped = convertToLabelValue(metaListRaw || []);
    setOptions(mapped);
    setInitialOptions(mapped);
    setFilteredOptions(mapped);
    setHasLoaded(true);
  }, [metaListRaw]);

  // Reset when dependent field changes
  useEffect(() => {
    if (!field.dependentOn) return;

    const prevValueStr = prevParentValueRef.current;
    prevParentValueRef.current = parentValueStr;

    if (field.strictDependency !== false && isParentEmpty) {
      // Parent cleared — reset everything
      setValue?.(field.id, "");
      setOptions([]);
      setInitialOptions([]);
      setFilteredOptions([]);
      selectedOptionRef.current = null;
      setHasLoaded(false);
    } else if (prevValueStr !== undefined && prevValueStr !== parentValueStr) {
      // Parent changed to a new value — reset so API/metadata re-fetches
      if (field.clearOnDependencyChange !== false) {
        setValue?.(field.id, "");
        selectedOptionRef.current = null;
        setInputValue("");
      }
      if (!field.list) {
        setOptions([]);
        setInitialOptions([]);
        setFilteredOptions([]);
        selectedOptionRef.current = null;
        setHasLoaded(false);
      }
    }
  }, [parentValueStr]);

  // Clear local selected reference when value cleared
  useEffect(() => {
    const watchedValue = watch?.(field.id);
    if (!watchedValue) {
      selectedOptionRef.current = null;
      setInputValue("");
    }
  }, [watch?.(field.id)]);

  const handleInitialLoad = async () => {
    if (field.list) return;               // 🔥 data comes from Redux metadata, no API call
    if (!field.query || !getData) return; // 🔥 stop API if no query
    if (hasLoaded) return;

    try {
      setLoading(true);
      setFilteredOptions([]);

      const res = await getData({
        variables: {
          input: {
            ...getDependentPayload(),
            ...(field.extraPayload || {}),
            limit: INITIAL_DATA_LIMIT,
            offset: 0,
          },
        },
      });

      let records =
        res?.data?.[field.gqlKey]?.[field.dataKey] || [];

      if (field.formatData) {
        records = field.formatData(records);
      }

      const mappedResults = convertToLabelValue(records);

      setOptions(mappedResults);
      setInitialOptions(mappedResults);
      setFilteredOptions(mergeSelectedOption(mappedResults));
      setHasLoaded(true);
    } catch (error) {
      console.error(`[${field.id}] Initial load error:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event: any) => {
    event.stopPropagation();
    const value = event.target.value;
    setInputValue(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length < 3) {
      setFilteredOptions(mergeSelectedOption(options));
      return;
    }

    debounceRef.current = setTimeout(async () => {
      if (!field.query || !getData) return;
      try {
        setLoading(true);

        const res = await getData({
          variables: {
            input: {
              ...(field.searchFieldKey
                ? { [field.searchFieldKey]: value }
                : {}),
              ...getDependentPayload(),
              ...(field.extraPayload || {}),
              limit: 30,
              offset: 0,
            },
          },
        });

        let records =
          res?.data?.[field.gqlKey]?.[field.dataKey] || [];

        if (field.formatData) {
          records = field.formatData(records);
        }

        const mappedResults = convertToLabelValue(records);
        setFilteredOptions(mergeSelectedOption(mappedResults));
      } catch (error) {
        console.error(`[${field.id}] Search error:`, error);
        setFilteredOptions([]);
      } finally {
        setLoading(false);
      }
    }, 600);
  };

  const handleClearSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setInputValue("");
    setFilteredOptions(mergeSelectedOption(options));
  };

  const hasOptions =
    filteredOptions && filteredOptions.length > 0;

  const singleFormError =
    errors && errors[field.id]?.message;

  return (
    <Grid item {...field.breakpoints}>
      <Controller
        name={field.id}
        control={control}
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <>
            <FormControl
              sx={{ width: "100%" }}
              size="small"
              className={classes.border}
              error={!!singleFormError}
            >
              <InputLabel>{field.label}</InputLabel>

              <StyledSelect
                value={value || ""}
                label={field.label}
                onOpen={handleInitialLoad}
                renderValue={(selected) => {
                const selectedItem = filteredOptions.find(
                  (o: any) => o.value === selected
                );

                const label = selectedItem?.label || "";

                return (
                  <span
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                    title={label}
                  >
                    {label}
                  </span>
                );
              }}
                onChange={(e) => {
                  const newValue = e.target.value;
                  onChange(newValue);

                  if (!newValue) {
                    selectedOptionRef.current = null;
                    setInputValue("");
                    setFilteredOptions(initialOptions);
                  } else {
                    const selected = filteredOptions.find(
                      (o: any) => o.value === newValue
                    );
                    if (selected) {
                      selectedOptionRef.current = selected;
                    }
                  }
                }}
                onClose={() => {
                  if (debounceRef.current) {
                    clearTimeout(debounceRef.current);
                  }
                  setInputValue("");
                  setFilteredOptions(
                    mergeSelectedOption(initialOptions)
                  );
                }}
                disabled={
                  field.disabled ||
                  (field.dependentOn && field.strictDependency !== false && isParentEmpty)
                }
                MenuProps={{
                  sx: {
                    "&& .MuiList-root": {
                      display: "flex",
                      flexDirection: "column",
                      padding: "10px",
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                <MenuItem disableRipple sx={{ py: 1, px: 0 }}>
                  <Box sx={{ width: "100%", px: 1 }}>
                    <TextField
                      label="Search here"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={inputValue}
                      onChange={handleSearchChange}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      autoFocus
                      placeholder="Type to search..."
                      InputProps={{
                        endAdornment: inputValue && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearSearch();
                            }}
                            sx={{ mr: -1 }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        ),
                      }}
                    />
                  </Box>
                </MenuItem>

                {loading ? (
                  <MenuItem disabled>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2">
                        {inputValue.length >= 3
                          ? "Searching..."
                          : "Loading..."}
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : !hasOptions ? (
                  <MenuItem disabled>
                    <Typography variant="body2">
                      No options found
                    </Typography>
                  </MenuItem>
                ) : null}

                {hasOptions &&
                  !field?.hideNone && (
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                  )}

                {filteredOptions.map((option: any) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                  >
                    <ListItemText
                      primary={option.label}
                    />
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>

            {singleFormError && (
              <FieldError field={field} errors={errors} />
            )}
          </>
        )}
      />
    </Grid>
  );
};

export default SingleSelectWithSearch;